const SELLING_PLAN_PURCHASE_TYPES = {
  SUBSCRIPTION: 'subscription',
  ONE_TIME: 'one_time',
};

const SELLING_PLAN_SELECTORS = {
  data: '[data-product-selling-plan-data]',
  sellingPlanInput: '[data-product-selling-plan-input]',
  variantCard: '[data-product-selling-plan-variant-card]',
  variantInput: '[data-product-selling-plan-variant-input]',
  variantPrice: '[data-product-selling-plan-variant-price]',
  purchaseCard: '[data-product-selling-plan-purchase-card]',
  purchaseInput: '[data-product-selling-plan-purchase-input]',
  price: '[data-product-selling-plan-price]',
  comparePrice: '[data-product-selling-plan-compare-price]',
  perBottle: '[data-product-selling-plan-per-bottle]',
  saveAmount: '[data-product-selling-plan-save-amount]',
  subscriptionTitle: '[data-product-selling-plan-subscription-title]',
  shipping: '[data-product-selling-plan-shipping]',
  benefitSavings: '[data-product-selling-plan-benefit-savings]',
  monthSelector: '[data-product-selling-plan-month-selector]',
  monthSelect: '[data-product-selling-plan-month-select]',
};

class ProductSellingPlanPicker extends HTMLElement {
  connectedCallback() {
    if (this.initialized || !this.loadProductData()) return;

    this.initialized = true;
    this.cacheElements();
    this.setInitialState();
    this.bindEvents();
    this.update();
  }

  loadProductData() {
    const dataElement = this.querySelector(SELLING_PLAN_SELECTORS.data);
    if (!dataElement) return false;

    this.productData = JSON.parse(dataElement.textContent);
    return true;
  }

  cacheElements() {
    this.productForm = this.closest('form');
    this.variantIdInput = this.productForm?.querySelector('input[name="id"]');
    this.sellingPlanIdInput = this.querySelector(SELLING_PLAN_SELECTORS.sellingPlanInput);
    this.variantOptionCards = Array.from(this.querySelectorAll(SELLING_PLAN_SELECTORS.variantCard));
    this.variantOptionInputs = Array.from(this.querySelectorAll(SELLING_PLAN_SELECTORS.variantInput));
    this.purchaseOptionCards = Array.from(this.querySelectorAll(SELLING_PLAN_SELECTORS.purchaseCard));
    this.purchaseOptionInputs = Array.from(this.querySelectorAll(SELLING_PLAN_SELECTORS.purchaseInput));
    this.monthSelects = Array.from(this.querySelectorAll(SELLING_PLAN_SELECTORS.monthSelect));
    this.submitButtons = this.findSubmitButtons();
  }

  setInitialState() {
    const checkedVariantInput = this.variantOptionInputs.find((input) => input.checked);
    const fallbackVariantId = this.productData.variants[0]?.id || '';

    this.selectedVariantId = checkedVariantInput?.value || String(fallbackVariantId);
    this.selectedPurchaseType = this.dataset.initialPurchaseType || SELLING_PLAN_PURCHASE_TYPES.ONE_TIME;
    this.selectedSellingPlanId = '';
    this.selectedFallbackSellingPlanId = '';
    this.normalizeSelection();
  }

  bindEvents() {
    this.variantOptionInputs.forEach((input) => {
      input.addEventListener('change', (event) => this.onVariantChange(event));
    });

    this.purchaseOptionInputs.forEach((input) => {
      input.addEventListener('change', (event) => this.onPurchaseTypeChange(event));
    });

    this.monthSelects.forEach((select) => {
      select.addEventListener('change', (event) => this.onMonthSelectChange(event));
    });
  }

  onVariantChange(event) {
    if (!event.currentTarget.checked) return;

    this.selectedVariantId = event.currentTarget.value;
    this.normalizeSelection();
    this.update();
    this.dispatchVariantChange();
  }

  onPurchaseTypeChange(event) {
    if (!event.currentTarget.checked) return;

    this.selectedPurchaseType = event.currentTarget.value;
    this.normalizeSelection();
    this.update();
  }

  onMonthSelectChange(event) {
    this.selectedFallbackSellingPlanId = event.currentTarget.value;
    this.normalizeSelection();
    this.update();
  }

  findSubmitButtons() {
    const buttons = new Set();
    const formButton = this.productForm?.querySelector('button[type="submit"][name="add"]');

    if (formButton) buttons.add(formButton);

    if (this.productForm?.id) {
      document.querySelectorAll(`button[type="submit"][form="${this.productForm.id}"]`).forEach((button) => {
        buttons.add(button);
      });
    }

    return Array.from(buttons);
  }

  findVariantById(id) {
    return this.productData.variants.find((variant) => String(variant.id) === String(id));
  }

  getSelectedVariant() {
    return this.findVariantById(this.selectedVariantId);
  }

  findMatchingSellingPlanAllocation(variant) {
    if (!variant?.allocations?.length) return null;

    const monthCount = this.getVariantMonthCount(variant);
    if (!monthCount) {
      return this.shouldShowMonthSelectorWithoutMetafield() ? this.findFallbackSellingPlanAllocation(variant) : null;
    }

    return variant.allocations.find((allocation) => this.allocationMatchesMonthCount(allocation, monthCount)) || null;
  }

  findFallbackSellingPlanAllocation(variant) {
    const monthOptions = this.getAllocationMonthOptions(variant);
    if (!monthOptions.length) return null;

    const selectedOption = monthOptions.find(
      (option) => String(option.allocation.sellingPlanId) === String(this.selectedFallbackSellingPlanId)
    );

    return (selectedOption || monthOptions[0]).allocation;
  }

  allocationMatchesMonthCount(allocation, monthCount) {
    const allocationText = `${allocation.name || ''} ${allocation.label || ''}`.toLowerCase();

    if (monthCount === 1) {
      return (
        allocationText.includes('monthly') ||
        allocationText.includes('every month') ||
        allocationText.includes('every 1 month') ||
        allocationText.includes('1 month')
      );
    }

    return allocationText.includes(`every ${monthCount} month`) || allocationText.includes(`${monthCount} month`);
  }

  normalizeSelection() {
    const selectedVariant = this.getSelectedVariant();
    if (!selectedVariant) return;

    this.normalizeFallbackSellingPlanSelection(selectedVariant);
    const matchingAllocation = this.findMatchingSellingPlanAllocation(selectedVariant);
    const requiresSellingPlan = this.productData.requiresSellingPlan || selectedVariant.requiresSellingPlan;

    if (requiresSellingPlan) {
      this.selectedPurchaseType = SELLING_PLAN_PURCHASE_TYPES.SUBSCRIPTION;
    } else if (this.selectedPurchaseType === SELLING_PLAN_PURCHASE_TYPES.SUBSCRIPTION && !matchingAllocation) {
      this.selectedPurchaseType = SELLING_PLAN_PURCHASE_TYPES.ONE_TIME;
    }

    this.selectedSellingPlanId = matchingAllocation?.sellingPlanId ? String(matchingAllocation.sellingPlanId) : '';
  }

  update() {
    const selectedVariant = this.getSelectedVariant();
    if (!selectedVariant) return;

    this.updateVariantCards();
    this.updatePurchaseOptions(selectedVariant);
    this.updateFormInputs(selectedVariant);
  }

  updateVariantCards() {
    this.variantOptionCards.forEach((card) => {
      const variant = this.findVariantById(card.dataset.variantId);
      const pricing = this.getPurchasePricing(variant, this.selectedPurchaseType);
      const isSelected = String(card.dataset.variantId) === String(this.selectedVariantId);

      card.classList.toggle('is-selected', isSelected);
      card.classList.toggle('is-disabled', !variant?.available);
      this.setTextAndVisibility(card, SELLING_PLAN_SELECTORS.variantPrice, this.getDiscountLabel(pricing));
    });
  }

  updatePurchaseOptions(selectedVariant) {
    this.purchaseOptionCards.forEach((card) => {
      const purchaseType = card.dataset.purchaseType;
      const pricing = this.getPurchasePricing(selectedVariant, purchaseType);
      const isSubscription = purchaseType === SELLING_PLAN_PURCHASE_TYPES.SUBSCRIPTION;
      const isSubscriptionUnavailable = isSubscription && !this.findMatchingSellingPlanAllocation(selectedVariant);
      const isSelected = purchaseType === this.selectedPurchaseType;

      this.updatePurchaseOptionState(card, isSelected, isSubscriptionUnavailable);
      this.updatePurchaseOptionContent(card, selectedVariant, pricing);
    });
  }

  updatePurchaseOptionState(card, isSelected, isDisabled) {
    const input = card.querySelector(SELLING_PLAN_SELECTORS.purchaseInput);

    card.classList.toggle('is-selected', isSelected);
    card.classList.toggle('is-disabled', isDisabled);

    if (input) {
      input.checked = isSelected;
      input.disabled = isDisabled;
    }
  }

  updatePurchaseOptionContent(card, selectedVariant, pricing) {
    this.updateMonthSelector(card, selectedVariant);
    this.setText(card, SELLING_PLAN_SELECTORS.price, pricing?.priceMoney);
    this.setTextAndVisibility(card, SELLING_PLAN_SELECTORS.comparePrice, pricing?.compareAtPriceMoney);
    this.setTextAndVisibility(card, SELLING_PLAN_SELECTORS.perBottle, this.getPerBottleLabel(pricing));
    this.setTextAndVisibility(card, SELLING_PLAN_SELECTORS.saveAmount, this.getSaveAmountLabel(pricing));
    this.setText(card, SELLING_PLAN_SELECTORS.subscriptionTitle, selectedVariant.title);
    this.setText(card, SELLING_PLAN_SELECTORS.shipping, this.getShipmentLabel(selectedVariant));
    this.setText(card, SELLING_PLAN_SELECTORS.benefitSavings, this.getSavingsEveryOrderLabel(pricing?.discountPercent || 0));
  }

  updateFormInputs(selectedVariant) {
    this.updateVariantIdInput(selectedVariant);
    this.updateSellingPlanIdInput();
    this.updateSubmitButtons(selectedVariant);
  }

  updateVariantIdInput(selectedVariant) {
    if (!this.variantIdInput) return;

    this.variantIdInput.value = selectedVariant.id;
    this.variantIdInput.disabled = !selectedVariant.available;
  }

  updateSellingPlanIdInput() {
    if (!this.sellingPlanIdInput) return;

    const shouldSubmitSellingPlan =
      this.selectedPurchaseType === SELLING_PLAN_PURCHASE_TYPES.SUBSCRIPTION && this.selectedSellingPlanId;

    if (shouldSubmitSellingPlan) {
      this.sellingPlanIdInput.name = 'selling_plan';
      this.sellingPlanIdInput.value = this.selectedSellingPlanId;
      this.sellingPlanIdInput.disabled = false;
      return;
    }

    this.sellingPlanIdInput.removeAttribute('name');
    this.sellingPlanIdInput.value = '';
    this.sellingPlanIdInput.disabled = true;
  }

  updateSubmitButtons(selectedVariant) {
    const isSubscriptionInvalid =
      this.selectedPurchaseType === SELLING_PLAN_PURCHASE_TYPES.SUBSCRIPTION && !this.selectedSellingPlanId;
    const shouldDisable = !selectedVariant.available || isSubscriptionInvalid;

    this.submitButtons.forEach((button) => {
      const label = button.querySelector('span');
      button.disabled = shouldDisable;
      if (label) label.textContent = selectedVariant.available ? this.dataset.addLabel : this.dataset.soldOutLabel;
    });
  }

  dispatchVariantChange() {
    const selectedVariant = this.getSelectedVariant();
    if (!selectedVariant) return;

    this.variantIdInput?.dispatchEvent(new Event('change', { bubbles: true }));
    document.dispatchEvent(new CustomEvent('variant:change', { detail: { variant: selectedVariant } }));
  }

  getPurchasePricing(variant, purchaseType) {
    if (!variant) return null;

    const allocation =
      purchaseType === SELLING_PLAN_PURCHASE_TYPES.SUBSCRIPTION
        ? this.findMatchingSellingPlanAllocation(variant)
        : null;
    const price = allocation?.price || variant.price;
    const comparePricing = this.getComparePricing(variant, allocation, price);
    const discountPercent =
      comparePricing.price > price ? Math.round(((comparePricing.price - price) / comparePricing.price) * 100) : 0;

    return {
      price,
      priceMoney: allocation?.priceMoney || variant.priceMoney,
      compareAtPrice: comparePricing.price,
      compareAtPriceMoney: comparePricing.money,
      saveAmountMoney: allocation ? allocation.saveAmountMoney : variant.saveAmountMoney,
      perBottleMoney: this.getPerBottleMoney(variant, allocation, price),
      discountPercent,
    };
  }

  getPerBottleMoney(variant, allocation, price) {
    const existingPerBottleMoney = allocation ? allocation.perBottleMoney : variant.perBottleMoney;
    if (existingPerBottleMoney) return existingPerBottleMoney;

    const monthCount = this.getEffectiveMonthCount(variant, allocation);
    if (!monthCount || !price) return '';

    return this.formatMoney(Math.round(price / monthCount));
  }

  getComparePricing(variant, allocation, price) {
    const candidates = [
      { price: allocation?.compareAtPrice, money: allocation?.compareAtPriceMoney },
      { price: variant.compareAtPrice, money: variant.compareAtPriceMoney },
      { price: variant.price, money: variant.priceMoney },
    ];

    return candidates.find((candidate) => candidate.price > price) || { price: 0, money: '' };
  }

  getDiscountLabel(pricing) {
    if (!pricing?.discountPercent) return '';

    return this.dataset.saveTemplate.replace('[percent]', pricing.discountPercent);
  }

  getPerBottleLabel(pricing) {
    if (!pricing?.perBottleMoney) return '';

    return this.dataset.perBottleTemplate.replace('[price]', pricing.perBottleMoney);
  }

  getSaveAmountLabel(pricing) {
    if (!pricing?.saveAmountMoney) return '';

    return this.dataset.saveAmountTemplate.replace('[amount]', pricing.saveAmountMoney);
  }

  getSavingsEveryOrderLabel(percent) {
    return this.dataset.benefitSavingsTemplate.replace('[percent]', percent);
  }

  getShipmentLabel(variant) {
    const monthCount = this.getEffectiveMonthCount(variant, this.findMatchingSellingPlanAllocation(variant));
    if (monthCount === 1) return this.dataset.shipmentOneTemplate;
    if (monthCount > 1) return this.dataset.shipmentOtherTemplate.replaceAll('[count]', monthCount);
    return '';
  }

  getVariantMonthCount(variant) {
    const monthCount = Number(variant?.monthCount || 0);
    return Number.isFinite(monthCount) ? monthCount : 0;
  }

  getEffectiveMonthCount(variant, allocation = null) {
    const variantMonthCount = this.getVariantMonthCount(variant);
    if (variantMonthCount) return variantMonthCount;

    return this.getAllocationMonthCount(allocation || this.findFallbackSellingPlanAllocation(variant));
  }

  getAllocationMonthCount(allocation) {
    const allocationText = `${allocation?.name || ''} ${allocation?.label || ''}`.toLowerCase();
    const explicitMatch = allocationText.match(/every\s+(\d+)\s*month/) || allocationText.match(/(\d+)\s*month/);

    if (explicitMatch) return Number(explicitMatch[1]);
    if (allocationText.includes('monthly') || allocationText.includes('every month')) return 1;
    return 0;
  }

  getAllocationMonthOptions(variant) {
    if (!variant?.allocations?.length) return [];

    const optionsByMonth = new Map();

    variant.allocations.forEach((allocation) => {
      const monthCount = this.getAllocationMonthCount(allocation);
      const label = this.getMonthOptionLabel(allocation);
      const key = monthCount ? `month-${monthCount}` : `label-${label}`;

      if (!optionsByMonth.has(key)) {
        optionsByMonth.set(key, { allocation, monthCount, label });
      }
    });

    return Array.from(optionsByMonth.values()).sort((firstOption, secondOption) => {
      if (!firstOption.monthCount) return 1;
      if (!secondOption.monthCount) return -1;
      return firstOption.monthCount - secondOption.monthCount;
    });
  }

  getMonthOptionLabel(allocation) {
    const monthCount = this.getAllocationMonthCount(allocation);

    if (monthCount === 1) return this.dataset.monthOptionOneTemplate;
    if (monthCount > 1) return this.dataset.monthOptionOtherTemplate.replaceAll('[count]', monthCount);
    return allocation?.label || allocation?.name || '';
  }

  normalizeFallbackSellingPlanSelection(variant) {
    if (this.getVariantMonthCount(variant) || !this.shouldShowMonthSelectorWithoutMetafield()) {
      this.selectedFallbackSellingPlanId = '';
      return;
    }

    const monthOptions = this.getAllocationMonthOptions(variant);
    if (!monthOptions.length) {
      this.selectedFallbackSellingPlanId = '';
      return;
    }

    const hasSelectedOption = monthOptions.some(
      (option) => String(option.allocation.sellingPlanId) === String(this.selectedFallbackSellingPlanId)
    );

    if (!hasSelectedOption) {
      this.selectedFallbackSellingPlanId = String(monthOptions[0].allocation.sellingPlanId);
    }
  }

  updateMonthSelector(card, selectedVariant) {
    const wrapper = card.querySelector(SELLING_PLAN_SELECTORS.monthSelector);
    const select = card.querySelector(SELLING_PLAN_SELECTORS.monthSelect);
    if (!wrapper || !select) return;

    const shouldShowSelector =
      !this.getVariantMonthCount(selectedVariant) && this.shouldShowMonthSelectorWithoutMetafield();
    const monthOptions = shouldShowSelector ? this.getAllocationMonthOptions(selectedVariant) : [];

    this.setHidden(wrapper, !monthOptions.length);
    if (!monthOptions.length) return;

    this.replaceMonthSelectOptions(select, monthOptions);
    select.value = this.selectedFallbackSellingPlanId;
  }

  replaceMonthSelectOptions(select, monthOptions) {
    const currentValues = Array.from(select.options).map((option) => option.value).join(',');
    const nextValues = monthOptions.map((option) => String(option.allocation.sellingPlanId)).join(',');
    if (currentValues === nextValues) return;

    select.replaceChildren(
      ...monthOptions.map((monthOption) => {
        const option = document.createElement('option');
        option.value = String(monthOption.allocation.sellingPlanId);
        option.textContent = monthOption.label;
        return option;
      })
    );
  }

  formatMoney(cents) {
    if (window.Shopify?.formatMoney) return window.Shopify.formatMoney(cents);
    return '';
  }

  shouldShowMonthSelectorWithoutMetafield() {
    return this.dataset.showMonthSelectorWithoutMetafield === 'true';
  }

  setText(root, selector, value = '') {
    root.querySelectorAll(selector).forEach((element) => {
      element.textContent = value || '';
    });
  }

  setTextAndVisibility(root, selector, value = '') {
    root.querySelectorAll(selector).forEach((element) => {
      element.textContent = value || '';
      this.setHidden(element, !value);
    });
  }

  setHidden(element, shouldHide) {
    element.classList.toggle('hidden', Boolean(shouldHide));
  }
}

if (!customElements.get('product-selling-plan-picker')) {
  customElements.define('product-selling-plan-picker', ProductSellingPlanPicker);
}
